/**
 * useActiveContextOverride — composer-side selector that mirrors the
 * "explicit > implicit" precedence rule applied in ``useSendQuery``
 * (Jun 17, 2026).
 *
 * Single source of truth for the precedence: BOTH the send hook and
 * the composer hint subscribe to this rule, so flipping the rule (e.g.
 * "also drop active_context when the user attaches a file") is a
 * one-line change here. Drift between "what the BE sees" and "what
 * the composer hint says" is impossible by construction.
 *
 * Returns:
 *   - ``active``: the current ``ActiveContext`` (or null when no
 *     viewer is open).
 *   - ``isOverridden``: ``true`` when an active context exists AND
 *     the user has set an explicit scope on this conversation, which
 *     causes the active context to be DROPPED from the next send.
 *     The composer renders a small hint when this is true so the
 *     user understands why "have inbox open + ambito selected"
 *     answers about the folder, not the inbox.
 */
import {
  useConnectSpaceScope,
  useFolderFilter,
  useScopedDocuments,
} from "@/features/chat/hooks/useComposerConfig";
import { useActiveContextStore } from "../state/activeContextStore";

import type { ActiveContext } from "../types";

export interface ActiveContextOverrideResult {
  readonly active: ActiveContext | null;
  readonly isOverridden: boolean;
  /**
   * ``true`` when the user clicked the ambient chip's ✕ for the currently
   * open entity — the context is suppressed from the next send by explicit
   * user choice (distinct from ``isOverridden``, which is suppression by an
   * explicit scope). Both cases drop ``active_context`` on the wire.
   */
  readonly isDismissed: boolean;
  /** Dismiss the open entity from the next send(s) (the chip's ✕). */
  readonly dismiss: () => void;
}

export function useActiveContextOverride(): ActiveContextOverrideResult {
  const active = useActiveContextStore((s) => s.active);
  const dismissedId = useActiveContextStore((s) => s.dismissedId);
  const dismiss = useActiveContextStore((s) => s.dismiss);
  // Granular selectors — only re-render the banner when these three
  // slices flip, not on every composer-mode/persona/connection change.
  const folderFilter = useFolderFilter();
  const scopedDocuments = useScopedDocuments();
  const connectSpace = useConnectSpaceScope();

  if (!active) {
    return { active: null, isOverridden: false, isDismissed: false, dismiss };
  }

  const isDismissed = dismissedId === active.id;

  // Mirror the EXACT predicate from ``useSendQuery``:
  //   - sticky scopedDocuments or Connect Space → always suppress
  //     (strong explicit signals).
  //   - folderFilter → suppress UNLESS the open entity's ``folderIds``
  //     intersect the selected folders (convergent scope). When they
  //     converge the sidebar reinforces the folder scope rather than
  //     contradicting it — the user shouldn't see an override banner.
  //
  // Mentions are excluded here on purpose — they're inline-per-send
  // (no persistent state) so the banner can't anticipate them
  // accurately. The send hook still drops active_context when a
  // mention is present; the banner just won't preview that transient
  // case (we'd surface a banner that disappears the moment the user
  // sends, which is more noise than help).
  const hasStrongExplicit =
    scopedDocuments.length > 0 || Boolean(connectSpace?.id);
  const hasExplicitFolder = folderFilter.length > 0;
  const folderIntersects =
    hasExplicitFolder &&
    (active.folderIds?.some((id) => folderFilter.includes(id)) ?? false);

  const isOverridden =
    hasStrongExplicit || (hasExplicitFolder && !folderIntersects);

  return { active, isOverridden, isDismissed, dismiss };
}
